/**
 * Social media component
 * This displays buttons on single pages/posts to like this particular page
 */

// Import statements
import React from "react"
import {useLocation} from '@reach/router';

/**
 * Create SocialMedia non-page component
 * @param title
 * @param featuredImage
 * @returns {JSX.Element}
 * @constructor
 */
const SocialMedia = ({title, featuredImage}) => {

    // get location information
    const location = useLocation();

    // define image url
    const image_url = location.origin + featuredImage?.data?.images?.fallback?.src || ``;

    // return JSX component
    return (
        <div id="social_media_slider">
            <div className="btn-group w-100 d-flex" role="group" aria-label="..." id="social_media_slider_buttons">

                <ShareLink
                    href={`https://www.facebook.com/sharer/sharer.php?u=${location.href}&title=${title}`}
                    id="facebook"
                    label="Delen op Facebook"
                    pagePath={location.href}
                    title={title}>
                    <LinkLabel icon_class="icon icon-facebook-1"/>
                    <LinkLabel id="facebook_count"/>
                </ShareLink>

                <ShareLink
                    href={`whatsapp://send?text=${location.href}`}
                    id="whatsapp"
                    label="Delen op Whatsapp"
                    pagePath={location.href}
                    title={title}>
                    <LinkLabel icon_class="icon icon-whatsapp"/>
                </ShareLink>

                <ShareLink
                    href={`https://twitter.com/intent/tweet?status=${title}+${location.href}`}
                    id="twitter"
                    label="Delen op Twitter"
                    pagePath={location.href}
                    title={title}>
                    <LinkLabel icon_class="icon icon-twitter-1"/>
                </ShareLink>

                <ShareLink
                    href={`https://pinterest.com/pin/create/bookmarklet/?media=${image_url}&url=${location.href}&is_video=false&description=${title}`}
                    id="pinterest"
                    label="Delen op Pinterest"
                    pagePath={location.href}
                    title={title}>
                    <LinkLabel icon_class="icon icon-pinterest-1"/>
                    <LinkLabel id="pinterest_count"/>
                </ShareLink>

                <EmailLink
                    href={`/`}
                    id="mail"
                    label="Delen per e-mail"
                    title={title}>
                    <LinkLabel icon_class="icon icon-mail"/>
                </EmailLink>

            </div>
        </div>
    )
}

/**
 * Create a social media link to share this post (recipe/hotspot/post/book)
 * @param href
 * @param id
 * @param label
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */
const ShareLink = ({href, id, label, children, pagePath, title}) => {

    let onClick = '';
    if (id === 'facebook') {
        onClick = event => {
            trackSocialEvent(event, 'social', 'Facebook', 'Like', pagePath);
        }
    } else if (id === 'whatsapp') {
        onClick = event => {
            trackSocialEvent(event, 'social', 'Whatsapp', 'share', pagePath);
        }
    } else if (id === 'twitter') {
        onClick = event => {
            trackSocialEvent(event, 'social', 'Twitter', 'Tweet', pagePath);
        }
    } else if (id === 'pinterest') {
        onClick = event => {
            trackSocialEvent(event, 'social', 'Pinterest', 'Pin', pagePath);
        }
    }

    return (
        <a className="btn btn-grey-transparent" href={href} role="button" id={id} rel="noopener external"
           aria-label={label} onClick={onClick}>
            {children}
        </a>
    )
}

/**
 * Create a link to notify a friend by mail
 * @param href
 * @param id
 * @param label
 * @param children
 * @returns {JSX.Element}
 * @constructor
 */
const EmailLink = ({href, id, label, children, title}) => {
    return (
        <a className="btn btn-grey-transparent" href={href} role="button" id={id} rel="noopener external"
           aria-label={label} onClick={e => {
            composeMail(e, title)
        }}>
            {children}
        </a>
    )
}

/**
 * Create an icon component for social media sharing link
 * @param icon_class
 * @param id
 * @returns {JSX.Element}
 * @constructor
 */
const LinkLabel = ({icon_class, id}) => {
    return (
        <span className={icon_class} id={id}></span>
    )
}

/**
 * Function to print the page
 * @param strikeState
 * @param index
 */
function composeMail(event, title) {

    // Don't follow the link
    event.preventDefault();

    // send event to google analytics
    typeof window !== "undefined" && window.gtag('event', 'Share by email', {
        'event_category': 'Recipe',
        'event_label': title
    });

    // open mail client
    window.location.href = "mailto:?to=&subject=Lekker recept van Myfoodblog.nl";
}

// Track social event with Google Analytics
function trackSocialEvent(e, link, hitType, eventCategory, eventAction, eventLabel) {

    // abandon if link already aborted
    if (e.defaultPrevented) {
        return;
    }

    // abandon if analytics is not available
    if (typeof ga !== "function") {
        return;
    }

    // get the link
    let hyperlink = e.target.closest("a");

    // store the href in variable
    let href = hyperlink.getAttribute('href');

    // the new window stored in a variable to prevent being blocked by pop-up blocker
    let loadPagePath = window.open(href);

    // tracker has not yet run
    let run_already = false;

    // loadPage to open link in same window after tracker
    let loadPage = function () {
        // run only once
        if (run_already) {
            return;
        }
        run_already = true;

        loadPagePath();
    };

    // cancel event and record outbound link
    e.preventDefault();

    // Track event in Google Analytics
    typeof window !== "undefined" && window.gtag(
        'event', eventAction, {
            'event_category': eventCategory, 'event_label': eventLabel, 'event_callback': function () {
                // Load the original post in the browser
                loadPage();
            }
        });

    // redirect after one second if recording takes too long
    setTimeout(loadPage(), 1000);

    // cancel event
    e.preventDefault ? e.preventDefault() : e.returnValue = !1;
}

/**
 * Export SocialMedia constant
 */
export default SocialMedia
